/**
 * @fileoverview Aggregation selector for the desktop FlyingMenu filter bar.
 * Delegates all aggregation state to FilterContext.
 * @module shared/components/filters/AggregationSelector
 */
import { ReactElement } from 'react'
import { CalendarClock, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useFilter } from '@/shared/hooks/useFilter'
import type { AggregationType } from '@/shared/context/FilterContext'
import { AGGREGATION_TRANSLATION_KEYS, AGGREGATION_OPTIONS } from '@/shared/i18n/keyMaps'

/**
 * Aggregation selector backed by FilterContext.
 */
export function AggregationSelector(): ReactElement {
  const { t } = useTranslation('common')
  const { aggregation, setAggregation } = useFilter()
  const aggregationLabel = t(AGGREGATION_TRANSLATION_KEYS[aggregation])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="glass-button !h-10 !px-2 md:!px-3 lg:!px-4 py-2 flex items-center md:space-x-1.5 lg:space-x-2">
          <CalendarClock className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium text-muted-foreground hidden md:inline text-xs lg:text-sm">{aggregationLabel}</span>
          <ChevronDown className="w-4 h-4 text-muted-foreground hidden md:inline" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="glass-card">
        <DropdownMenuRadioGroup
          value={aggregation}
          onValueChange={value => setAggregation(value as AggregationType)}
        >
          {AGGREGATION_OPTIONS.map(option => (
            <DropdownMenuRadioItem key={option} value={option}>
              {t(AGGREGATION_TRANSLATION_KEYS[option])}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
